import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, Router } from '@angular/router';
import { FeatureHubService } from '@ion/feature-toggling';
import { map, Observable } from 'rxjs';

/**
 * The featureHubGuard checks if a feature is enabled.
 * If the feature is not enabled, user is redirected to the 404 page.
 */
export function onlyWhenFeatureHubGuard(featureHubKey: string) {
  return (): Observable<boolean | RedirectCommand> => {
    const featureHubService = inject(FeatureHubService);
    const router = inject(Router);

    const feature = featureHubService.observe(featureHubKey);
    return feature.pipe(
      map(isFeatureEnabled => {
        if (!isFeatureEnabled) {
          const redirectPath = router.parseUrl('/404');
          return new RedirectCommand(redirectPath);
        }
        return true;
      }),
    );
  };
}

/**
 * The redirectWhenFeatureHubGuard checks if a feature is enabled.
 * If the feature is enabled, user is redirected to the specified path/route.
 * If the feature is disabled, navigation proceeds normally (returns true).
 */
export function redirectWhenFeatureHubGuard(
  featureHubKey: string,
  redirectTo: string | ((route: ActivatedRouteSnapshot) => string | unknown[]),
) {
  return (route: ActivatedRouteSnapshot): Observable<boolean | RedirectCommand> => {
    const featureHubService = inject(FeatureHubService);
    const router = inject(Router);

    const feature = featureHubService.observe(featureHubKey);
    return feature.pipe(
      map(isFeatureEnabled => {
        if (isFeatureEnabled) {
          const target = typeof redirectTo === 'function' ? redirectTo(route) : redirectTo;
          const targetCommands = Array.isArray(target) ? target : [target];
          const redirectPath = router.createUrlTree(targetCommands, { queryParams: route.queryParams });
          return new RedirectCommand(redirectPath);
        }
        return true;
      }),
    );
  };
}
